import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  useEmployeeMe,
  useEmployeeUpdateAccountSettings,
} from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import getTimezones from "@/services/timezone-service.ts";
import { ApiEmployeeAccountSettingsSavePayload } from "@/types/employees/employee.types.ts";
import { LanguageOptions, Languages } from "@/types/organization.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { ButtonIcon, FontAwesomeIcon, Page, Select, Stack, useToasts } from "@skailer-com/uikit";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const AccountSettingsLocalisation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: employee } = useEmployeeMe();
  const { canEditField } = usePermissions();
  const { showInfo } = useToasts();
  const updateAccountSettings = useEmployeeUpdateAccountSettings();
  const { control, reset } = useForm<{
    language?: Languages;
    timezone?: string | null;
  }>();

  useTitle(t("core:account_localisation"));

  useEffect(() => {
    if (!employee) return;
    reset({
      language: employee.language,
      timezone: employee.timezone,
    });
  }, [employee, reset]);

  const availableLanguages = useMemo(
    () =>
      employee
        ? LanguageOptions.filter((option) =>
            employee.available_languages.includes(String(option.id))
          )
        : [],
    [employee]
  );
  const canEditTimezone = canEditField(employee, EmployeeFieldPermission.employee_timezone);
  const saveLocalisationSettings = (payload: ApiEmployeeAccountSettingsSavePayload) => {
    updateAccountSettings.mutate(payload, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.employees.accountSettingsEdited);
        showInfo({ title: t("common:changes_saved_successfully") });
      },
    });
  };

  return (
    <Page>
      <Page.Header
        backSlot={
          <ButtonIcon
            variant={"secondary"}
            icon={<FontAwesomeIcon icon={"fa-light fa-arrow-left"} />}
            onClick={() => navigate("/account-settings")}
          />
        }
        title={t("core:account_localisation")}
      />
      <Page.Content>
        <Stack className={"container-sm mx-auto"} gap={"md"}>
          <Stack className={"card-panel-sm"}>
            <Controller
              control={control}
              name={"language"}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value}
                  onChange={(nextValue) => {
                    const language = nextValue as Languages | null;
                    onChange(language);
                    if (language) saveLocalisationSettings({ language });
                  }}
                  label={t("common:language")}
                  options={availableLanguages}
                  emptyTitle={t("common:select")}
                  isLoading={updateAccountSettings.isPending}
                />
              )}
            />
            {canEditTimezone && (
              <Controller
                control={control}
                name={"timezone"}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    onChange={(nextValue) => {
                      const timezone = nextValue as string | null;
                      onChange(timezone);
                      if (timezone) saveLocalisationSettings({ timezone });
                    }}
                    label={t("common:timezone")}
                    options={getTimezones()}
                    emptyTitle={t("common:select")}
                    searchable
                    isLoading={updateAccountSettings.isPending}
                  />
                )}
              />
            )}
          </Stack>
        </Stack>
      </Page.Content>
    </Page>
  );
};
